import { forwardRef, useCallback, useEffect, useId, useRef, type HTMLAttributes, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { IconButton } from "./IconButton";
import { cn } from "@/lib/cn";
import { useEscapeStack } from "./overlayStack";

// Stage 5: local replacement for @datasirpi/ui's <Drawer>. Ported
// letter-for-letter from the vendor's compiled bundle and
// Drawer-SVOR3VRK.css — shares Modal's dialog semantics (focus trap,
// scroll lock, Escape close, focus restore) but slides in from an edge.
// Same tooling caveat as Modal.tsx: this behavior isn't verifiable by
// tsc/eslint/build/CSS-diff, only by a manual browser check.
//
// Note the different default from Modal: dismissOnOverlay defaults to
// FALSE here (Modal defaults true) — the vendor's own reasoning, preserved
// verbatim: an accidental backdrop click shouldn't discard in-progress
// work in a drawer the way it's fine to for a simple confirmation modal.
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export type DrawerSide = "start" | "end" | "top" | "bottom";
export type DrawerSize = "sm" | "md" | "lg" | "full";

export interface DrawerProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  side?: DrawerSide;
  size?: DrawerSize;
  dismissOnOverlay?: boolean;
  showCloseButton?: boolean;
  container?: HTMLElement | null;
  children?: ReactNode;
}

export const Drawer = forwardRef<HTMLDivElement, DrawerProps>(function Drawer(
  { open, onClose, title, description, footer, side = "end", size = "md", dismissOnOverlay = false, showCloseButton = true, container, className, children, ...rest },
  ref
) {
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const descId = description ? `${baseId}-desc` : undefined;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      panelRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref]
  );

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = (document.activeElement as HTMLElement) ?? null;
    return () => {
      previouslyFocused.current?.focus({ preventScroll: true });
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => {
      if (!panelRef.current) return;
      const first = panelRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (first ?? panelRef.current).focus({ preventScroll: true });
    }, 0);
    return () => clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  /* Phase 4.5 — Escape goes through the shared overlay stack, so only the
     TOP-most overlay closes. The listener this replaces was bound to
     `document` and called e.stopPropagation(), which does nothing between
     listeners on the same node: every open Modal and Drawer received the
     same Escape and all of them closed at once, so a confirm dialog
     dismissed the form behind it (finding F16). */
  useEscapeStack(open, onClose);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !panelRef.current) return;
    const focusables = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.hasAttribute("disabled")
    );
    if (focusables.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || !panelRef.current.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  if (!open) return null;

  function onOverlayMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (!dismissOnOverlay) return;
    if (e.target === e.currentTarget) onClose();
  }

  const portalTarget = container ?? (typeof document !== "undefined" ? document.body : null);
  if (!portalTarget) return null;

  return createPortal(
    <>
      <div className="rc-drawer-overlay" onMouseDown={onOverlayMouseDown} />
      <div
        ref={setRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
        data-side={side}
        data-size={size}
        className={cn("rc-drawer", className)}
        onKeyDown={onKeyDown}
        {...rest}
      >
        <header className="rc-drawer__header">
          <div>
            <h2 id={titleId} className="rc-drawer__title">
              {title}
            </h2>
            {description ? (
              <p id={descId} className="rc-drawer__description">
                {description}
              </p>
            ) : null}
          </div>
          {showCloseButton ? (
            <IconButton aria-label="Close drawer" icon={<X />} size="sm" onClick={onClose} className="rc-drawer__close" />
          ) : null}
        </header>
        <div className="rc-drawer__body">{children}</div>
        {footer ? <footer className="rc-drawer__footer">{footer}</footer> : null}
      </div>
    </>,
    portalTarget
  );
});
